import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismButton } from '@mylighthouse/prism-ember';
import type { PowerCalendarDay } from 'ember-power-calendar/utils';
import type { SmartInsightDay } from 'lumen/models';

import styles from './day-smart-insights.module.css';

interface MarketSmartInsightsCalendarDaySmartInsightsSignature {
  Args: {
    day: PowerCalendarDay;
    dayClass: string;
    insightCountsByStayDate: Record<string, number>;
    insightsByStayDate: Record<string, SmartInsightDay[]>;
    maxInsightEntriesPerDay: number;
    selectSmartInsightsDay: (stayDate: string) => void;
  };
}

export default class MarketSmartInsightsCalendarDaySmartInsights extends Component<MarketSmartInsightsCalendarDaySmartInsightsSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare portfolioSmartInsights: Services['portfolio-smart-insights'];
  @service declare usageAnalytics: Services['usage-analytics'];

  get classesForButton(): string {
    const { insightsSharePercentage, showInsight } = this;

    const classes: string[] = [];

    if (showInsight) {
      classes.push('ember-power-calendar-day--with-insights');
    }

    if (insightsSharePercentage > 0.66) {
      classes.push(styles['day-bucket-high']);
    } else if (insightsSharePercentage > 0.33) {
      classes.push(styles['day-bucket-normal']);
    } else {
      classes.push(styles['day-bucket-low']);
    }

    return classes.join(' ');
  }

  get countOfInsightsForThisDay(): number {
    return this.args.insightCountsByStayDate[this.args.day.id] || 0;
  }

  get dayNumberAsString(): string {
    const { day } = this.args;

    return `${day.number}`;
  }

  get insightsSharePercentage(): number {
    return this.countOfInsightsForThisDay / this.args.maxInsightEntriesPerDay;
  }

  get isDisabled(): boolean {
    const minDate = this.hotelAndMarketService.getLocalTime().startOf('day');
    const maxDate = minDate
      .add(this.portfolioSmartInsights.leadtime, 'day')
      .startOf('day');

    const { day } = this.args;
    const dayDate = this.hotelAndMarketService
      .toLocalTime(day.date.toDateString())
      .startOf('day');

    return dayDate.isBefore(minDate) || dayDate.isAfter(maxDate);
  }

  get showInsight(): boolean {
    return this.countOfInsightsForThisDay > 0 && !this.isDisabled;
  }

  @action onClickDay(): void {
    const { selectSmartInsightsDay, day } = this.args;

    selectSmartInsightsDay(day.id);

    this.usageAnalytics.track('Smart Insights Column Focused', {
      eventTrigger: 'smart-insights-calendar',
      product: 'smart-insights-multi-property',
      date: day.id,
    });
  }

  <template>
    {{#if this.showInsight}}
      <PrismButton
        @buttonType="ghost"
        @label={{this.dayNumberAsString}}
        class="{{@dayClass}} {{this.classesForButton}}"
        {{on "click" this.onClickDay}}
      />
    {{else}}
      <div
        class="text-200--regular flex items-center justify-center gap-100
          {{@dayClass}}
          {{if this.isDisabled 'ember-power-calendar-day--disabled'}}"
      >
        {{this.dayNumberAsString}}
      </div>
    {{/if}}
  </template>
}
